/**
 * User Authentication State Management
 * Global signals for user, authentication, and subscription state
 */
import { signal, effect } from '@preact/signals';
import { safeLocal } from '../utils/safeStorage.js';
import { getCurrentUser } from '../services/authService.js';

// User state
export const currentUser = signal(null);
export const isAuthenticated = signal(false);
export const isLoading = signal(true);
export const authError = signal(null);

// Subscription state
export const subscription = signal(null);
export const subscriptionTier = signal('free');
export const subscriptionStatus = signal('inactive');
export const approvalStatus = signal(null);

/**
 * Backend-authoritative admin data-scope info, fed from
 * GET /api/v1/admin/me/permissions (see services/adminService.js
 * getMyPermissions() and its caller in pages/AdminPortalPage.jsx). Replaces
 * the old `window.location.hostname.includes('staging')` guess that only
 * answered "which server am I talking to", not "whose data am I looking at".
 * Shape: { environment: string|null, analytics_reads_production: boolean }
 */
export const adminEnvironment = signal({ environment: null, analytics_reads_production: false });

/**
 * Set adminEnvironment from a GET /me/permissions response payload.
 */
export function setAdminEnvironment({ environment, analytics_reads_production } = {}) {
  adminEnvironment.value = {
    environment: environment ?? null,
    analytics_reads_production: !!analytics_reads_production,
  };
}

/**
 * Server-resolved admin-portal tabs, fed from the `tabs` field of
 * GET /api/v1/admin/me/permissions (PERMISSION_TAB_MAP in backend
 * app/permissions.py — the single source of truth for tab gating since
 * 2026-07). `null` = not fetched yet; canAccessTab() then falls back to the
 * legacy hardcoded role map so a cached admin session renders without a flash.
 */
export const adminTabs = signal(null);

/**
 * Set adminTabs from a GET /me/permissions response payload.
 */
export function setAdminTabs(tabs) {
  adminTabs.value = Array.isArray(tabs) ? tabs : null;
}

/**
 * Server-resolved admin permissions, fed from the `permissions` field of
 * GET /api/v1/admin/me/permissions (ROLE_PERMISSIONS in backend
 * app/permissions.py). `null` = not fetched yet. Arrives in the SAME response
 * as `tabs`, so any component rendered behind canAccessTab() already has it.
 *
 * Why this exists: gating an action button on `getAdminRole() === 'admin'`
 * hardcodes the answer to a question the backend already answers per
 * permission, and it silently diverges the moment a role is added — which is
 * exactly what happened to the Create Demo User button (visible only to
 * `admin`, while the endpoint accepted anyone with users.read). Gate on the
 * permission the endpoint actually enforces.
 */
export const adminPermissions = signal(null);

/**
 * Set adminPermissions from a GET /me/permissions response payload.
 */
export function setAdminPermissions(permissions) {
  adminPermissions.value = Array.isArray(permissions) ? permissions : null;
}

/**
 * Does the current admin hold `permission` (a Permission value string, e.g.
 * 'users.demo_create')? Server-resolved list wins.
 *
 * While the /me/permissions response is in flight we fall back to
 * `role === 'admin'` — ADMIN holds every Permission by construction
 * (ROLE_PERMISSIONS[ADMIN] = frozenset(Permission)), so the fallback can only
 * ever under-report, never grant something the server would refuse. Other
 * roles simply see the action appear once the response lands.
 */
export function hasAdminPermission(permission) {
  if (adminPermissions.value !== null) return adminPermissions.value.includes(permission);
  return getAdminRole() === 'admin';
}

/**
 * Initialize user state from localStorage or API
 */
export async function initializeAuth() {
  // Fast check: if no token, immediately set not authenticated
  const token = safeLocal.get('access_token');
  
  if (!token) {
    console.log('[UserState] No token found - user not authenticated');
    isAuthenticated.value = false;
    currentUser.value = null;
    subscription.value = null;
    isLoading.value = false;
    authError.value = null;
    return;
  }

  // Token exists - set loading and fetch user data
  isLoading.value = true;
  authError.value = null;

  try {

    console.log('[UserState] Token found - fetching user data...');

    // Try to get user from localStorage first (fast)
    const cachedUser = safeLocal.get('current_user');
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser);
        currentUser.value = user;
        subscription.value = user.subscription || null;
        updateSubscriptionState(user.subscription);
        isAuthenticated.value = true;
        console.log('[UserState] Loaded cached user:', user.email);
      } catch (e) {
        console.warn('[UserState] Failed to parse cached user:', e);
      }
    }

    // Fetch fresh data from API
    console.log('[UserState] Fetching fresh user data from API...');
    const user = await getCurrentUser();
    currentUser.value = user;
    subscription.value = user.subscription || null;
    updateSubscriptionState(user.subscription);
    isAuthenticated.value = true;
    console.log('[UserState] ✓ User authenticated:', user.email);
    
  } catch (error) {
    console.error('[UserState] ❌ Auth initialization failed:', error);
    authError.value = error.message;
    isAuthenticated.value = false;
    currentUser.value = null;
    subscription.value = null;
    
    // Clear invalid tokens
    safeLocal.remove('access_token');
    safeLocal.remove('refresh_token');
    safeLocal.remove('current_user');
  } finally {
    isLoading.value = false;
  }
}

/**
 * Update subscription-related signals
 */
function updateSubscriptionState(sub) {
  if (!sub) {
    subscriptionTier.value = 'free';
    subscriptionStatus.value = 'inactive';
    approvalStatus.value = null;
    return;
  }

  subscriptionTier.value = sub.tier || 'free';
  subscriptionStatus.value = sub.status || 'inactive';
  approvalStatus.value = sub.approval_status || null;
}

/**
 * Clear user state on logout
 */
export function clearUserState() {
  currentUser.value = null;
  isAuthenticated.value = false;
  subscription.value = null;
  subscriptionTier.value = 'free';
  subscriptionStatus.value = 'inactive';
  approvalStatus.value = null;
  authError.value = null;
  adminTabs.value = null;
}

/**
 * Check if user has specific subscription tier or higher
 */
export function hasSubscriptionTier(requiredTier) {
  const tierOrder = ['free', 'basic', 'pro', 'enterprise'];
  const currentTierIndex = tierOrder.indexOf(subscriptionTier.value);
  const requiredTierIndex = tierOrder.indexOf(requiredTier);
  
  return currentTierIndex >= requiredTierIndex;
}

/**
 * Check if user is admin/superuser (has any admin role)
 */
export function isAdmin() {
  return !!currentUser.value?.admin_role;
}

/**
 * Get the user's admin role
 */
export function getAdminRole() {
  return currentUser.value?.admin_role || null;
}

/**
 * Check if the user may give in-chat feedback on Ion responses — independent
 * of admin_role (e.g. a Knowledge Expert without any admin role).
 */
export function hasFeedbackAccess() {
  return !!currentUser.value?.feedback_access;
}

/**
 * Single source of truth for who may give in-chat Ion feedback. Mirrors the
 * backend gate (require_feedback_capture_access): a FULL admin OR the
 * feedback_access flag. NOT isAdmin() — that is true for any portal role
 * (marketing/finance/customer_success/knowledge_expert), which the backend
 * rejects with 403, so gating the UI on isAdmin() would show a button that fails.
 */
export function canGiveIonFeedback() {
  return getAdminRole() === 'admin' || hasFeedbackAccess();
}

/**
 * Fallback tab map, used ONLY while the /me/permissions response is in
 * flight (adminTabs still null). The authoritative map is the server's
 * PERMISSION_TAB_MAP (backend app/permissions.py), pinned against this one
 * by tests/unit/test_permissions_matrix.py — keep the two in sync when a
 * tab is added or regated.
 */
const FALLBACK_TAB_MAP = {
  admin: ['users', 'subscriptions', 'system', 'analytics', 'ai-management', 'promo-codes', 'audit-log', 'conversion-feedback', 'demo'],
  marketing: ['analytics', 'conversion-feedback'],
  customer_success: ['analytics', 'users', 'subscriptions', 'conversion-feedback'],
  finance: ['subscriptions', 'promo-codes'],
  // Application specialists provision demo accounts: exactly these two tabs.
  application_specialist: ['users', 'subscriptions'],
  // Ion knowledge experts reach ONLY the AI-Management tab; within it the
  // subtabs are further gated to Knowledge + AI Flows + My Submissions (see
  // AdminAiManagementTab).
  knowledge_expert: ['ai-management'],
};

/**
 * Check if the current admin can access a specific tab. Server-resolved
 * tabs (adminTabs signal) win; the hardcoded role map only bridges the
 * gap until GET /me/permissions responds.
 */
export function canAccessTab(tabId) {
  const role = currentUser.value?.admin_role;
  if (!role) return false;
  if (adminTabs.value !== null) return adminTabs.value.includes(tabId);
  return FALLBACK_TAB_MAP[role]?.includes(tabId) || false;
}

/**
 * Data-scope map for the admin tab bar — a NEW, parallel, read-only map
 * keyed the same way as the role-gating TAB_MAP above, NOT a modification
 * of who can see a tab. Mirrors the backend routing decision documented in
 * admin-env-indicator-guardrails-plan.md: exactly three admin routers
 * (analytics.py, cell_access_analytics.py, annotations.py) call
 * get_analytics_db_session(), which reads the PRODUCTION Postgres on
 * staging (analytics_db_host set); funnel_metrics.py and engagement_kpi.py
 * deliberately stay env-local. 'analytics' is tagged 'prod-live' because the
 * funnel dashboard tab mixes local + prod-redirect sub-endpoints. Safe to
 * hardcode — this only changes on the same lifecycle as TAB_MAP itself (a
 * new admin tab being added).
 */
const TAB_DATA_SOURCE = {
  analytics: 'prod-live', // funnel dashboard mixes local + prod-redirect sub-endpoints
  users: 'env-local',
  subscriptions: 'env-local',
  system: 'env-local',
  'ai-management': 'env-local',
  'promo-codes': 'env-local',
  'audit-log': 'env-local',
  'conversion-feedback': 'env-local',
  demo: 'env-local',
};

/**
 * Get the data-scope label ('prod-live' | 'env-local') for a given admin
 * tab id. Presentation-layer only — combine with `adminEnvironment` to
 * decide whether a page is ACTUALLY reading production right now (a
 * 'prod-live' tab only reads production when `analytics_reads_production`
 * is true, i.e. on staging).
 */
export function getTabDataSource(tabId) {
  return TAB_DATA_SOURCE[tabId] || 'env-local';
}

/**
 * Check if subscription is active (not expired, cancelled, etc.)
 * Case-insensitive check for ACTIVE or TRIALING status
 */
export function hasActiveSubscription() {
  const status = subscriptionStatus.value?.toUpperCase();
  return status === 'ACTIVE' || status === 'TRIALING';
}

/**
 * Check if subscription needs approval
 */
export function needsApproval() {
  return approvalStatus.value === 'pending';
}
